/**
 * /intermediarios — F-external-intermediary — Intermediários EXTERNOS (fora do
 * grupo) que ganham comissão. Company-scoped (ES e BS têm os seus). Cadastro
 * (Fase 1) + o pagamento é gerado aqui (Fase 3): proforma 'partner' somando
 * técnico + intermediário, espelhando o fluxo do grupo. Vínculo no par
 * técnico×cliente é feito no modal do técnico (Fase 2).
 */
import { useMemo, useState } from "react";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";

interface Draft {
  id: number | null;
  name: string;
  email: string;
  phone: string;
  countryCode: string;
  taxId: string;
  taxIdKind: string;
  notes: string;
}

const empty: Draft = {
  id: null,
  name: "",
  email: "",
  phone: "",
  countryCode: "FR",
  taxId: "",
  taxIdKind: "",
  notes: "",
};

const inputCls =
  "w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm focus:border-lime outline-none";

export default function Intermediarios() {
  const t = useT();
  const canEdit = useCanEdit();
  const companiesQuery = trpc.companies.list.useQuery();
  const companies = useMemo(() => companiesQuery.data ?? [], [companiesQuery.data]);
  const [companyId] = useSelectedCompany(companies);
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState<Draft | null>(null);
  const [payFor, setPayFor] = useState<{ id: number; name: string } | null>(null);

  const utils = trpc.useUtils();
  const listQ = trpc.externalIntermediaries.list.useQuery(
    {
      companyId: companyId ?? 0,
      q: q.trim() || undefined,
      active: showInactive ? undefined : true,
      limit: 200,
      offset: 0,
    },
    { enabled: companyId != null },
  );
  const invalidate = () => utils.externalIntermediaries.list.invalidate();

  const createMut = trpc.externalIntermediaries.create.useMutation({
    onSuccess: () => {
      void invalidate();
      setModal(null);
    },
  });
  const updateMut = trpc.externalIntermediaries.update.useMutation({
    onSuccess: () => {
      void invalidate();
      setModal(null);
    },
  });
  const setActiveMut = trpc.externalIntermediaries.setActive.useMutation({
    onSuccess: () => void invalidate(),
  });

  const rows = listQ.data?.rows ?? [];
  const saving = createMut.isPending || updateMut.isPending;
  const err = createMut.error?.message ?? updateMut.error?.message ?? null;

  function save() {
    if (!modal || companyId == null || !modal.name.trim()) return;
    const payload = {
      companyId,
      name: modal.name.trim(),
      email: modal.email.trim() || undefined,
      phone: modal.phone.trim() || undefined,
      countryCode: modal.countryCode.trim().toUpperCase() || "FR",
      taxId: modal.taxId.trim() || undefined,
      taxIdKind: modal.taxIdKind.trim() || undefined,
      notes: modal.notes.trim() || undefined,
    };
    if (modal.id == null) createMut.mutate(payload);
    else updateMut.mutate({ ...payload, id: modal.id });
  }

  return (
    <ErpShell>
      <main className="max-w-screen-lg mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold">{t("intermediaries.title")}</h1>
            <p className="text-sm text-neutral-400">{t("intermediaries.subtitle")}</p>
          </div>
          {canEdit && (
            <button
              onClick={() => setModal({ ...empty })}
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium transition-colors"
            >
              {t("intermediaries.newBtn")}
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <input
            className={inputCls + " max-w-xs"}
            placeholder={t("intermediaries.searchPlaceholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <label className="flex items-center gap-1.5 text-xs text-neutral-400 cursor-pointer select-none">
            <input
              type="checkbox"
              className="accent-lime"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            {t("intermediaries.includeInactive")}
          </label>
        </div>

        <div className="rounded-lg border border-neutral-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-neutral-500 border-b border-neutral-800">
              <tr>
                <th className="px-3 py-2">{t("intermediaries.colName")}</th>
                <th className="px-3 py-2">{t("intermediaries.colEmail")}</th>
                <th className="px-3 py-2">{t("intermediaries.colCountry")}</th>
                <th className="px-3 py-2">{t("intermediaries.colTaxId")}</th>
                <th className="px-3 py-2">{t("intermediaries.colStatus")}</th>
                <th className="px-3 py-2 text-right">{t("intermediaries.colActions")}</th>
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-neutral-500">
                    {t("intermediaries.loading")}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-neutral-500">
                    {t("intermediaries.emptyState")}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-b border-neutral-900 hover:bg-neutral-800/30 ${r.active ? "" : "opacity-50"}`}
                  >
                    <td className="px-3 py-2 font-medium">{r.name}</td>
                    <td className="px-3 py-2 text-neutral-400">{r.email ?? "—"}</td>
                    <td className="px-3 py-2 text-neutral-400">{r.countryCode}</td>
                    <td className="px-3 py-2 text-neutral-400">
                      {r.taxId ? `${r.taxId}${r.taxIdKind ? ` (${r.taxIdKind})` : ""}` : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${
                          r.active ? "bg-lime/15 text-lime" : "bg-neutral-700/40 text-neutral-400"
                        }`}
                      >
                        {r.active
                          ? t("intermediaries.badgeActive")
                          : t("intermediaries.badgeInactive")}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        {canEdit && r.active && (
                          <button
                            onClick={() => setPayFor({ id: r.id, name: r.name })}
                            className="rounded-md bg-lime/15 text-lime hover:bg-lime hover:text-on-lime px-2 py-0.5 text-xs font-medium transition-colors"
                          >
                            {t("intermediaries.generatePaymentBtn")}
                          </button>
                        )}
                        {canEdit && (
                          <button
                            onClick={() =>
                              setModal({
                                id: r.id,
                                name: r.name,
                                email: r.email ?? "",
                                phone: r.phone ?? "",
                                countryCode: r.countryCode,
                                taxId: r.taxId ?? "",
                                taxIdKind: r.taxIdKind ?? "",
                                notes: r.notes ?? "",
                              })
                            }
                            className="rounded-md border border-neutral-700 hover:border-lime hover:text-lime px-2 py-0.5 text-xs transition-colors"
                          >
                            {t("intermediaries.editBtn")}
                          </button>
                        )}
                        {canEdit && companyId != null && (
                          <button
                            onClick={() =>
                              setActiveMut.mutate({ companyId, id: r.id, active: !r.active })
                            }
                            disabled={setActiveMut.isPending}
                            className="rounded-md border border-neutral-700 hover:border-amber-500 hover:text-amber-300 px-2 py-0.5 text-xs transition-colors disabled:opacity-40"
                          >
                            {r.active
                              ? t("intermediaries.deactivateBtn")
                              : t("intermediaries.reactivateBtn")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {modal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-lg bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl">
            <div className="px-5 py-4 border-b border-neutral-800">
              <h2 className="text-lg font-semibold">
                {modal.id == null
                  ? t("intermediaries.modalTitleNew")
                  : t("intermediaries.modalTitleEdit")}
              </h2>
            </div>
            <div className="px-5 py-5 space-y-3">
              <div>
                <label className="block text-xs text-neutral-400 mb-1">
                  {t("intermediaries.fieldName")}
                </label>
                <input
                  className={inputCls}
                  value={modal.name}
                  onChange={(e) => setModal({ ...modal, name: e.target.value })}
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-neutral-400 mb-1">
                    {t("intermediaries.fieldEmail")}
                  </label>
                  <input
                    className={inputCls}
                    type="email"
                    value={modal.email}
                    onChange={(e) => setModal({ ...modal, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-400 mb-1">
                    {t("intermediaries.fieldPhone")}
                  </label>
                  <input
                    className={inputCls}
                    value={modal.phone}
                    onChange={(e) => setModal({ ...modal, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-neutral-400 mb-1">
                    {t("intermediaries.fieldCountry")}
                  </label>
                  <input
                    className={inputCls}
                    maxLength={2}
                    value={modal.countryCode}
                    onChange={(e) =>
                      setModal({ ...modal, countryCode: e.target.value.toUpperCase() })
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-400 mb-1">
                    {t("intermediaries.fieldTaxId")}
                  </label>
                  <input
                    className={inputCls}
                    value={modal.taxId}
                    onChange={(e) => setModal({ ...modal, taxId: e.target.value })}
                    placeholder={t("intermediaries.fieldTaxIdPlaceholder")}
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-400 mb-1">
                    {t("intermediaries.fieldTaxIdKind")}
                  </label>
                  <input
                    className={inputCls}
                    value={modal.taxIdKind}
                    onChange={(e) => setModal({ ...modal, taxIdKind: e.target.value })}
                    placeholder="siret / tva / cnpj"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-neutral-400 mb-1">
                  {t("intermediaries.fieldNotes")}
                </label>
                <textarea
                  className={inputCls + " resize-none"}
                  rows={2}
                  value={modal.notes}
                  onChange={(e) => setModal({ ...modal, notes: e.target.value })}
                />
              </div>
              {err && <p className="text-xs text-red-400">{err}</p>}
            </div>
            <div className="px-5 py-4 border-t border-neutral-800 flex justify-end gap-2">
              <button
                onClick={() => setModal(null)}
                className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm transition-colors"
              >
                {t("intermediaries.cancelBtn")}
              </button>
              <button
                onClick={save}
                disabled={saving || !modal.name.trim()}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {saving ? t("intermediaries.savingBtn") : t("intermediaries.saveBtn")}
              </button>
            </div>
          </div>
        </div>
      )}

      {payFor && companyId != null && (
        <PaymentModal companyId={companyId} intermediary={payFor} onClose={() => setPayFor(null)} />
      )}
    </ErpShell>
  );
}

const eur = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });

function PaymentModal({
  companyId,
  intermediary,
  onClose,
}: {
  companyId: number;
  intermediary: { id: number; name: string };
  onClose: () => void;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const [result, setResult] = useState<
    null | { reference: string; issuerCompanyId: number; itemCount: number; total: number }[]
  >(null);

  const previewQ = trpc.proformaRequests.previewForExternalIntermediary.useQuery(
    { companyId, externalIntermediaryId: intermediary.id },
    { enabled: result == null },
  );

  const genMut = trpc.proformaRequests.createForExternalIntermediary.useMutation({
    onSuccess: (data) => {
      setResult(data.created);
      void utils.proformaRequests.invalidate();
    },
  });

  const preview = previewQ.data;
  const eligible = preview?.eligible ?? [];
  const blocked = preview?.blocked ?? [];
  const awaiting = preview?.awaitingCustomer ?? [];

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-2xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-neutral-800">
          <h2 className="text-lg font-semibold">
            {t("intermediaries.payModalTitle")} — {intermediary.name}
          </h2>
          <p className="text-xs text-neutral-400 mt-0.5">{t("intermediaries.payModalSubtitle")}</p>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {result != null ? (
            <div className="space-y-3">
              <p className="text-sm text-lime font-medium">
                {result.length === 1
                  ? t("intermediaries.proformaGeneratedSingular")
                  : `${result.length} ${t("intermediaries.proformasGeneratedSuf")}`}
              </p>
              <div className="rounded-lg border border-neutral-800 divide-y divide-neutral-900">
                {result.map((p) => (
                  <div
                    key={p.reference}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                  >
                    <span className="font-medium">{p.reference}</span>
                    <span className="text-neutral-400">
                      {p.itemCount} {t("intermediaries.dossierCount")} · {eur.format(p.total)}
                    </span>
                  </div>
                ))}
              </div>
              <a
                href="/proformas"
                className="inline-block text-xs text-lime underline decoration-lime/40 hover:decoration-lime"
              >
                {t("intermediaries.viewInProformas")}
              </a>
            </div>
          ) : previewQ.isLoading ? (
            <p className="text-sm text-neutral-500">{t("intermediaries.loadingEligible")}</p>
          ) : previewQ.error ? (
            <p className="text-sm text-red-400">{previewQ.error.message}</p>
          ) : (
            <>
              {eligible.length === 0 ? (
                <p className="text-sm text-neutral-400">{t("intermediaries.noEligible")}</p>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-400">
                      {eligible.length} {t("intermediaries.dossierCount")}
                      {preview && preview.issuerCount > 1
                        ? ` · ${preview.issuerCount} ${t("intermediaries.payingCompaniesSuf")}`
                        : ""}
                    </span>
                    <span className="font-semibold text-lime">
                      {t("intermediaries.totalLabel")} {eur.format(preview?.totalEligible ?? 0)}
                    </span>
                  </div>
                  <div className="rounded-lg border border-neutral-800 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left text-xs uppercase text-neutral-500 border-b border-neutral-800">
                        <tr>
                          <th className="px-3 py-2">{t("intermediaries.colDossier")}</th>
                          <th className="px-3 py-2">{t("intermediaries.colPlate")}</th>
                          <th className="px-3 py-2">{t("intermediaries.colTechnician")}</th>
                          <th className="px-3 py-2">{t("intermediaries.colCustomer")}</th>
                          <th className="px-3 py-2 text-right">{t("intermediaries.colAmount")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligible.map((it) => (
                          <tr
                            key={`${it.dossierId}-${it.technicianId}`}
                            className="border-b border-neutral-900"
                          >
                            <td className="px-3 py-1.5">{it.dossierCode ?? `#${it.dossierId}`}</td>
                            <td className="px-3 py-1.5 text-neutral-400">{it.plate ?? "—"}</td>
                            <td className="px-3 py-1.5 text-neutral-400">{it.technicianName}</td>
                            <td className="px-3 py-1.5 text-neutral-400">
                              {it.customerName ?? "—"}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums">
                              {eur.format(it.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {blocked.length > 0 && (
                <div className="text-xs text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2">
                  {blocked.length} {t("intermediaries.blockedSuf")}{" "}
                  {blocked.map((b) => b.dossierCode ?? `#${b.dossierId}`).join(", ")}.
                </div>
              )}

              {/* F-ext-interm-gate (CEO 2026-09-30) — carro só é pago depois que o
                  cliente pagou; os demais ficam visíveis, fora da proforma. */}
              {awaiting.length > 0 && (
                <div className="text-xs text-neutral-300 bg-neutral-900 border border-neutral-800 rounded-md px-3 py-2">
                  ⏳ {awaiting.length} {t("intermediaries.awaitingSuf")}{" "}
                  {eur.format(awaiting.reduce((s, a) => s + a.amount, 0))}:{" "}
                  {awaiting.map((b) => b.plate ?? b.dossierCode ?? `#${b.dossierId}`).join(", ")}.
                </div>
              )}

              {genMut.error && <p className="text-xs text-red-400">{genMut.error.message}</p>}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-neutral-800 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm transition-colors"
          >
            {result != null ? t("intermediaries.closeBtn") : t("intermediaries.cancelBtn")}
          </button>
          {result == null && (
            <button
              onClick={() => genMut.mutate({ companyId, externalIntermediaryId: intermediary.id })}
              disabled={genMut.isPending || eligible.length === 0}
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {genMut.isPending
                ? t("intermediaries.generatingBtn")
                : t("intermediaries.generateProformasBtn")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
